import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { CheckCircle, ExpandMore, Psychology, TrendingUp, EmojiEvents, Speed } from '@mui/icons-material';
import StripeCheckoutButton from '../components/StripeCheckoutButton';

const BehavioralQuestionsPage: React.FC = () => {

  const painPoints = [
    "You're tired of being caught off guard by behavioral questions in technical interviews",
    "You panic when asked to describe a challenging project or conflict resolution",
    "Your mind goes blank when interviewers ask about leadership or teamwork experiences", 
    "You struggle to articulate your experiences in a structured, compelling way",
    "You're losing job opportunities because you nail the technical part but bomb the behavioral",
    "You feel like you're winging it instead of having a systematic approach to interview prep"
  ];

  const benefits = [
    {
      icon: <Psychology sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Eliminate Interview Anxiety',
      description: 'Know exactly what questions are coming and have proven frameworks to answer them confidently'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Hyper-Focused Preparation',
      description: 'Stop wasting time on generic prep. Focus on the 10 questions that matter most'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Strategic Self-Marketing',
      description: 'Turn every behavioral question into an opportunity to showcase your unique value'
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Stand Out from Competition',
      description: 'Master the storytelling techniques that make interviewers remember you'
    }
  ];

  const includedItems = [
    "The 10 most common behavioral questions (with statistical backing)",
    "STAR framework templates for each question type",
    "30+ example stories across different scenarios",
    "Red flags to avoid that instantly kill your chances",
    "Advanced techniques to weave in technical skills naturally",
    "Salary negotiation follow-up strategies"
  ];

  const faqs = [
    {
      question: "What if I don't have enough experience to answer these questions?",
      answer: "The guide includes frameworks for adapting your existing experiences, whether from internships, school projects, or personal coding projects. You'll learn how to extract compelling stories from experiences you already have."
    },
    {
      question: "How is this different from generic interview prep resources?",
      answer: "This focuses specifically on the 10 most frequently asked behavioral questions in technical interviews, with proven frameworks and real examples. It's laser-focused on what actually gets asked, not theoretical preparation."
    },
    {
      question: "Can I use this for non-technical roles too?",
      answer: "While designed for technical interviews, the STAR frameworks and storytelling principles work for any behavioral interview. However, the examples and context are specifically tailored for software engineering roles."
    },
    {
      question: "How quickly can I implement this?",
      answer: "You can start using the frameworks immediately. Most people see dramatic improvement in their interview confidence within 24 hours of implementation."
    },
    {
      question: "What if I'm not satisfied?",
      answer: "Join our $7 community to get this for free, plus access to live coaching calls, resume reviews, and a supportive community of engineers advancing their careers."
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      
      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="md">
          {/* Audience Callout */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h6"
              sx={{ color: 'secondary.main', mb: 2, fontWeight: 'bold' }}
            >
              For Software Engineers Who Ace Technical Rounds But Struggle With Behavioral Questions
            </Typography>
          </Box>

          {/* Bold Promise */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
            >
              <span style={{ color: '#88C0D0' }}>Master Every Behavioral Interview</span> With Confidence
            </Typography>
          </Box>

          {/* Summary */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h5"
              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.4 }}
            >
              Get instant access to the 10 questions you'll be asked in every technical behavioral interview, proven STAR framework templates, 30+ real example stories, advanced self-marketing techniques, and salary negotiation follow-up strategies.
            </Typography>
          </Box>

          {/* CTA Buttons */}
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <StripeCheckoutButton
                priceId={process.env.REACT_APP_RESUME_GUIDE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB'}
                buttonText="Get It Now for $11"
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  color: 'black',
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  '&:hover': { transform: 'scale(1.02)' },
                  transition: 'transform 0.2s',
                }}
                successUrl="/success?product=behavioral-questions"
                cancelUrl="/10-behavioral-questions"
              />
              <Button
                component="a"
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  borderWidth: 2,
                  '&:hover': { 
                    borderWidth: 2,
                    transform: 'scale(1.02)' 
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get It FREE in $7 Community
              </Button>
            </Box>
          </Box>

          {/* Pain Points */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              If Any of This <span style={{ color: '#88C0D0' }}>Sounds Like You...</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {painPoints.map((point, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'rgba(136, 192, 208, 0.05)', 
                    border: '1px solid #333',
                    '&:hover': {
                      border: '1px solid #88C0D0',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      ❌ {point}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Benefits */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              Transform Into a <span style={{ color: '#88C0D0' }}>Confident Interviewer</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              {benefits.map((benefit, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'background.paper', 
                    border: '2px solid #333',
                    '&:hover': {
                      border: '2px solid #88C0D0',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                          {benefit.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Solution Introduction */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Introducing <span style={{ color: '#88C0D0' }}>10 Behavioral Questions You'll Be Asked</span>
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.5 }}
            >
              The systematically-researched, battle-tested preparation system that eliminates behavioral interview anxiety forever.
            </Typography>
          </Box>

          {/* Solution Discovery Story */}
          <Card sx={{ bgcolor: 'background.paper', border: '2px solid #333', mb: 8 }}>
            <CardContent sx={{ p: 6 }}>
              <Typography variant="h5" sx={{ color: 'secondary.main', mb: 4, textAlign: 'center' }}>
                How I Discovered This System
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                After coaching hundreds of engineers and analyzing thousands of interview experiences, I noticed a disturbing pattern. Engineers who could solve complex algorithms in minutes were getting rejected because of behavioral questions.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                I spent months analyzing successful interviews, studying what top performers did differently, and testing frameworks with my coaching clients. What I discovered was shocking: 90% of behavioral questions in technical interviews come from the same 10 core patterns.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                The engineers who knew this secret were landing offers at 3x the rate of those who didn't. They weren't necessarily better engineers - they just had better answers to predictable questions.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.8 }}>
                That's when I created this system. Not another generic interview guide, but a laser-focused toolkit for the exact questions you'll face. Because when you know what's coming, confidence becomes automatic.
              </Typography>
            </CardContent>
          </Card>

          {/* What's Included */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              What's <span style={{ color: '#88C0D0' }}>Included</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {includedItems.map((item, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'rgba(136, 192, 208, 0.1)', 
                    border: '2px solid #88C0D0',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircle sx={{ color: 'secondary.main', flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                        {item}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* FAQ */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              Frequently Asked <span style={{ color: '#88C0D0' }}>Questions</span>
            </Typography>
            
            {faqs.map((faq, index) => (
              <Accordion 
                key={index}
                sx={{ 
                  bgcolor: 'background.paper', 
                  border: '1px solid #333',
                  mb: 2,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                  sx={{ 
                    '&:hover': { bgcolor: 'rgba(136, 192, 208, 0.05)' }
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Final CTA */}
          <Paper
            sx={{
              p: 6,
              bgcolor: 'rgba(136, 192, 208, 0.1)',
              border: '3px solid #88C0D0',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{ 
                color: 'secondary.main', 
                mb: 4, 
                fontWeight: 'bold',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              Ready to Master Behavioral Interviews?
            </Typography>
            
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Get Your <span style={{ color: '#88C0D0' }}>Interview Advantage</span> Today
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
              <StripeCheckoutButton
                priceId={process.env.REACT_APP_RESUME_GUIDE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB'}

                buttonText="Get It Now for $11"
                variant="contained"
                color="secondary"
                size="large"
                successUrl="/success?product=behavioral-questions"
                cancelUrl="/10-behavioral-questions"
                sx={{
                  color: 'black',
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  '&:hover': { transform: 'scale(1.02)' },
                  transition: 'transform 0.2s',
                }}
              />
              <Button
                component="a"
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  borderWidth: 2,
                  '&:hover': { 
                    borderWidth: 2,
                    transform: 'scale(1.02)' 
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get It FREE in $7 Community
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Join engineers who've transformed their interview performance and landed their dream jobs
            </Typography>
          </Paper>

        </Container>
      </Box>
    </Box>
  );
};

export default BehavioralQuestionsPage;